'use client';

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import Link from 'next/link';
import { 
	Bot, 
	Brain, 
	Search, 
	MessageSquare, 
	Target, 
	CreditCard,
	Clock,
	TrendingUp,
	TrendingDown,
	Shield,
	Zap,
	AlertCircle,
	Send,
	Play,
	CheckCircle,
	BarChart3,
	Users,
	Mail,
	ArrowRight
} from 'lucide-react';

export function SolutionSection() {
	const benefits = [
		{
			icon: <Clock className="h-5 w-5 text-orange-500" />,
			title: '24/7 Operation',
			stat: '3x more opportunities',
			description: 'Never miss a lead again'
		},
		{
			icon: <TrendingUp className="h-5 w-5 text-orange-500" />,
			title: 'Consistent Performance',
			stat: '85% consistency rate',
			description: 'Same quality every time'
		},
		{
			icon: <Shield className="h-5 w-5 text-orange-500" />,
			title: 'Risk-Free Scaling',
			stat: '$0 hiring costs',
			description: 'Add agents instantly'
		},
		{
			icon: <Zap className="h-5 w-5 text-orange-500" />,
			title: 'Instant Deployment',
			stat: '15 minutes setup',
			description: 'Start selling today'
		}
	];

	return (
		<section className="py-20 px-4 sm:px-6 lg:px-8">
			<div className="max-w-7xl mx-auto">

				{/* How To Train Section */}
				<div className="text-center mb-16">
					<h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
						How To Train Your AI Agents
					</h2>
					<p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12">
						Simple, natural language training that transforms your conversations into powerful sales agents
					</p>
				</div>

				{/* Training Flow */}
				<div className="space-y-16 mb-20">
					{/* Step 1: Natural Language Training */}
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						<div>
							<div className="flex items-center mb-6">
								<div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">1</div>
								<h3 className="text-2xl font-bold text-gray-900">Train in Natural Language</h3>
							</div>
							<p className="text-lg text-gray-600 mb-6">
								No JSON, no technical knowledge required. Simply chat with our training interface about 
								how your agent should handle different sales scenarios.
							</p>
							<div className="space-y-3">
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>Conversational training interface</span>
								</div>
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>AI transforms conversations into optimized prompts</span>
								</div>
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>Real-time performance improvement</span>
								</div>
							</div>
						</div>
						<div>
							<Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
								<CardContent className="p-6">
									<div className="space-y-4">
										<div className="bg-white p-4 rounded-lg border-l-4 border-orange-500">
											<div className="text-sm font-medium text-orange-900 mb-2">Training Assistant</div>
											<p className="text-sm text-gray-700">"I notice your agent is struggling with pricing objections. How should it handle when prospects say the price is too high?"</p>
										</div>
										<div className="bg-orange-100 p-4 rounded-lg border-l-4 border-orange-600">
											<div className="text-sm font-medium text-orange-900 mb-2">You</div>
											<p className="text-sm text-gray-700">"First acknowledge their concern, then break down the ROI by showing how much they're currently losing without our solution..."</p>
										</div>
										<div className="text-center">
											<div className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
												<Zap className="h-3 w-3 mr-1" />
												AI optimizing system prompt...
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>

					{/* Step 2: MCP Integration */}
					<div className="grid lg:grid-cols-2 gap-12 items-center">
						<div className="lg:order-2">
							<div className="flex items-center mb-6">
								<div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">2</div>
								<h3 className="text-2xl font-bold text-gray-900">MCP Tools Automatically Integrated</h3>
							</div>
							<p className="text-lg text-gray-600 mb-6">
								Connect your MCP server and your tools become part of the training process. 
								Your agent learns to use your specific tools and data sources naturally.
							</p>
							<div className="space-y-3">
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>Automatic tool discovery and integration</span>
								</div>
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>Context-aware tool usage training</span>
								</div>
								<div className="flex items-center text-gray-700">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3" />
									<span>Real-time data and API connections</span>
								</div>
							</div>
						</div>
						<div className="lg:order-1">
							<Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
								<CardContent className="p-6">
									<div className="space-y-4">
										<div className="text-center mb-4">
											<div className="text-sm font-medium text-blue-900 mb-2">MCP Server Connected</div>
											<div className="flex justify-center space-x-2">
												<div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
												<span className="text-xs text-green-600">wss://your-mcp-server.com/mcp</span>
											</div>
										</div>
										<div className="grid grid-cols-2 gap-3">
											<div className="bg-white p-3 rounded border text-center">
												<Bot className="h-5 w-5 text-blue-600 mx-auto mb-1" />
												<div className="text-xs font-medium">Lead Finder</div>
											</div>
											<div className="bg-white p-3 rounded border text-center">
												<Search className="h-5 w-5 text-blue-600 mx-auto mb-1" />
												<div className="text-xs font-medium">Company Data</div>
											</div>
											<div className="bg-white p-3 rounded border text-center">
												<Mail className="h-5 w-5 text-blue-600 mx-auto mb-1" />
												<div className="text-xs font-medium">Email Sender</div>
											</div>
											<div className="bg-white p-3 rounded border text-center">
												<BarChart3 className="h-5 w-5 text-blue-600 mx-auto mb-1" />
												<div className="text-xs font-medium">Analytics</div>
											</div>
										</div>
										<div className="text-center">
											<div className="inline-flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
												<CheckCircle className="h-3 w-3 mr-1" />
												4 tools integrated into training
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</div>
					</div>
				</div>

				{/* Pricing Section */}
				<div className="text-center mb-16">
					<h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
						Early Access Program
					</h3>
					<p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
						Be among the first to train AI agents that find their own customers and get paid
					</p>
					
					<Card className="max-w-md mx-auto bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-lg">
						<CardContent className="p-8 text-center">
							<div className="mb-6">
								<div className="text-4xl font-bold text-gray-900 mb-2">$900<span className="text-lg text-gray-600 font-normal">/month</span></div>
								<p className="text-orange-600 font-medium">Early Access Pricing</p>
							</div>
							
							<div className="space-y-3 mb-8 text-left">
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
									<span className="text-gray-700">Natural language training interface</span>
								</div>
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
									<span className="text-gray-700">MCP tools auto-integration</span>
								</div>
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
									<span className="text-gray-700">Unlimited agent training</span>
								</div>
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
									<span className="text-gray-700">24/7 autonomous sales execution</span>
								</div>
								<div className="flex items-center">
									<CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
									<span className="text-gray-700">Direct founder support</span>
								</div>
							</div>
							
							<a 
								href="https://cal.com/up-punyagupta/chat-with-founders" 
								target="_blank" 
								rel="noopener noreferrer"
								className="w-full inline-flex items-center justify-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold py-3 px-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
							>
								Start Early Access
								<ArrowRight className="h-5 w-5 ml-2" />
							</a>
						</CardContent>
					</Card>
				</div>

				{/* Don't Have MCP Section */}
				<div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-8 md:p-12 mb-16 text-center border border-orange-200">
					<h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
						Don't have an MCP but got an Amazing AI?
					</h3>
					<p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
						We'll help you integrate your existing AI system with our training platform. 
						Get the best of both worlds - your powerful AI with our proven sales methodology.
					</p>
					<a 
						href="https://cal.com/up-punyagupta/chat-with-founders" 
						target="_blank" 
						rel="noopener noreferrer"
						className="inline-flex items-center bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
					>
						<Users className="h-5 w-5 mr-2" />
						Contact Us - Chat with Founders
						<ArrowRight className="h-5 w-5 ml-2" />
					</a>
				</div>

			</div>
		</section>
	);
}